import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Pie, Bar, Doughnut, getElementAtEvent } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './Dashboard.css';
import { getReportsApiBaseUrl } from '../../../config/reportsApi';
import { getNodeServiceBaseUrl } from '../../../config/apiBase';
import { EmptyState } from '../../../shared-ui/States';
 
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, ChartDataLabels);

// --- Cache Configuration ---
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const CACHE_KEYS = {
  WORK_ORDERS: 'work_orders_cache',
  ALL_WORK_ORDERS_STATUS: 'all_work_orders_status_cache', // Key for the new unfiltered data
  INVOICES: 'invoices_cache',
  CUSTOMERS: 'customers_cache',
  USERS: 'users_cache',
  ASSETS: 'assets_cache'
};

// --- In-Memory Cache Implementation ---
const cache = {
  data: {},
  
  set(key, value) {
    this.data[key] = {
      value,
      timestamp: Date.now()
    };
  },
  
  get(key) {
    const cached = this.data[key];
    if (!cached) return null;
    
    const isExpired = (Date.now() - cached.timestamp) > CACHE_DURATION;
    if (isExpired) {
      delete this.data[key];
      return null;
    }
    
    return cached.value;
  },
  
  clear(key) {
    if (key) {
      delete this.data[key];
    } else {
      this.data = {};
    }
  },
  
  clearAll() {
    this.data = {};
  }
};

// ====================================================================
// --- Reusable Chart Component with Options ---
// ====================================================================
const ChartWithOptions = ({ title, chartType, onChartTypeChange, onExport, onFullScreen, children, cardClassName = '' }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const wrapperRef = useRef(null);
    const [chartTypeSubMenuOpen, setChartTypeSubMenuOpen] = useState(false);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setMenuOpen(false);
                setChartTypeSubMenuOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const handleTypeChange = (type) => {
        onChartTypeChange(type);
        setMenuOpen(false);
        setChartTypeSubMenuOpen(false);
    };

    return (
        <div className={`chart-card ${cardClassName}`}>
            <div className="chart-card-header">
                <h3>{title}</h3>
                <div className="chart-options-wrapper" ref={wrapperRef}>
                    <button className="chart-options-btn" onClick={() => setMenuOpen(!menuOpen)}>
                        ⋮
                    </button>
                    {menuOpen && (
                        <div className="chart-options-menu">
                            <div 
                                className="chart-options-menu-item with-submenu"
                                onMouseEnter={() => setChartTypeSubMenuOpen(true)}
                                onMouseLeave={() => setChartTypeSubMenuOpen(false)}
                            >
                                <span>Graph Type</span>
                                <span className="submenu-arrow">▶</span>
                                {chartTypeSubMenuOpen && (
                                    <div className="chart-options-submenu">
                                        <button className={`submenu-item ${chartType === 'bar' ? 'active' : ''}`} onClick={() => handleTypeChange('bar')}>Bar</button>
                                        <button className={`submenu-item ${chartType === 'pie' ? 'active' : ''}`} onClick={() => handleTypeChange('pie')}>Pie</button>
                                        <button className={`submenu-item ${chartType === 'doughnut' ? 'active' : ''}`} onClick={() => handleTypeChange('doughnut')}>Donut</button>
                                        <button className="submenu-item disabled" title="Not available">Packed Bubble</button>
                                    </div>
                                )}
                            </div>
                            <button className="chart-options-menu-item" onClick={onFullScreen}>Full Screen</button>
                            <button className="chart-options-menu-item" onClick={onExport}>Export</button>
                        </div>
                    )}
                </div>
            </div>
            <div className="chart-content">
                {children}
            </div>
        </div>
    );
};

// ====================================================================
// --- Component for Work Order Table ---
// ====================================================================
const FilteredWorkOrderTable = ({ workOrders, apiToken }) => {
    // ... (This component remains unchanged)
    const [activeTab, setActiveTab] = useState('assets');
    const [assets, setAssets] = useState([]);
    const [assetsLoading, setAssetsLoading] = useState(false);
    const [assetsError, setAssetsError] = useState(null);
 
    useEffect(() => {
        const fetchAssets = async () => {
            if (!workOrders || workOrders.length === 0) {
                setAssets([]);
                return;
            }
            const assetIdSet = new Set(workOrders.map(wo => wo.asset_id).filter(id => id != null));
            const assetIds = Array.from(assetIdSet);
            if (assetIds.length === 0) {
                setAssets([]);
                return;
            }
           
            const cacheKey = `${CACHE_KEYS.ASSETS}_${assetIds.sort().join('_')}`;
            const cached = cache.get(cacheKey);
            if (cached) {
                console.log('Using cached assets data for work orders');
                setAssets(cached);
                return;
            }

            setAssetsLoading(true);
            setAssetsError(null);
           
            const ASSET_API_URL = `${process.env.REACT_APP_ASSET_SERVICE_BASE_URL}/api/assets/`;
            const ASSET_HEADERS = { 'accept': 'application/json', 'Authorization': apiToken };
 
            try {
                const assetPromises = assetIds.map(id =>
                    fetch(`${ASSET_API_URL}${id}`, { headers: ASSET_HEADERS }).then(res => {
                        if (!res.ok) {
                            console.error(`Failed to fetch asset ${id}, status: ${res.status}`);
                            return null;
                        }
                        return res.json();
                    })
                );
                const fetchedAssetsResults = await Promise.all(assetPromises);
                const validAssets = fetchedAssetsResults.filter(asset => asset !== null);
                
                cache.set(cacheKey, validAssets);
                setAssets(validAssets);
            } catch (error) {
                console.error("Failed to fetch assets:", error);
                setAssetsError(error.message);
            } finally {
                setAssetsLoading(false);
            }
        };
        fetchAssets();
    }, [workOrders, apiToken]);
 
    const displayedWorkOrders = useMemo(() => {
        if (!workOrders || !Array.isArray(workOrders)) return [];
        return [...workOrders].sort((a, b) => new Date(b.add_date) - new Date(a.add_date));
    }, [workOrders]);
 
    const formatData = (value) => {
        if (value === null || typeof value === 'undefined') return 'N/A';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'string' && value.includes('T')) {
            const date = new Date(value);
            if (!isNaN(date)) return date.toLocaleDateString('en-GB');
        }
        return value;
    };
 
    const displayColumns = [
        { header: 'Doc No', key: 'doc_no' }, { header: 'WO No', key: 'wo_no' },
        { header: 'Status', key: 'wo_open_close' }, { header: 'Total Value', key: 'tot_value' },
        { header: 'Customer Name', key: 'customer_name' }, { header: 'Customer ID', key: 'cust_id' },
        { header: 'Project Name', key: 'project_name' }, { header: 'Project ID', key: 'project_id' },
        { header: 'Sector Name', key: 'sector_name' }, { header: 'Sector ID', key: 'sector' },
        { header: 'Asset ID', key: 'asset_id' }, { header: 'WO Date', key: 'wo_date' },
        { header: 'WO Start', key: 'wo_start' }, { header: 'WO End', key: 'wo_end' },
    ];
 
    return (
        <div className="filtered-table-container">
            <div className="table-header"><h3>All Work Orders</h3></div>
            <div className="table-controls">
                <div className="tabs">
                    <button className={`tab-btn ${activeTab === 'assets' ? 'active' : ''}`} onClick={() => setActiveTab('assets')}>Assets</button>
                    <button className={`tab-btn ${activeTab === 'workorder' ? 'active' : ''}`} onClick={() => setActiveTab('workorder')}>Workorder</button>
                </div>
            </div>
            <div className="table-content-wrapper">
                {activeTab === 'workorder' ? (
                    <>
                        {displayedWorkOrders.length > 0 ? (
                            <div className="table-scroll-container">
                                <table className="data-table wide-table">
                                    <thead><tr>{displayColumns.map(col => <th key={col.key}>{col.header}</th>)}</tr></thead>
                                    <tbody>
                                        {displayedWorkOrders.map((order, index) => (
                                            <tr key={`${order.id}-${index}`}>
                                                {displayColumns.map(col => (
                                                    <td key={col.key}>{col.key === 'tot_value' ? parseFloat(order[col.key] || 0).toLocaleString('en-US') : formatData(order[col.key])}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <EmptyState
                                title="No work orders"
                                description="No work orders match the current chart filter. Clear filters or adjust your selection."
                            />
                        )}
                    </>
                ) : (
                    <>
                        {assetsLoading && <p className="loading-message">Loading Assets...</p>}
                        {assetsError && <p className="error-message">Error: {assetsError}</p>}
                        {!assetsLoading && !assetsError && (
                            <>
                                {assets.length > 0 ? (
                                    <div className="table-scroll-container">
                                        <table className="data-table wide-table">
                                            <thead>
                                                <tr><th>Asset Name</th><th>Asset Type</th><th>Capacity (MT)</th><th>Year of Purchase</th><th>Year of Manufacture</th></tr>
                                            </thead>
                                            <tbody>
                                                {assets.map(asset => {
                                                    const combinedAssetName = [asset.name, asset.serial_number, asset.asset_code, asset.registration_number].filter(Boolean).join(' | ');
                                                    return (
                                                        <tr key={asset.id}>
                                                            <td>{combinedAssetName || 'N/A'}</td>
                                                            <td>{formatData(asset.asset_type)}</td>
                                                            <td>{formatData(asset.capacity_mt)}</td>
                                                            <td>{formatData(asset.year_of_purchase)}</td>
                                                            <td>{formatData(asset.year_of_manufacture)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (<p className="no-data-message">No assets found for the current work orders.</p>)}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
 
// ====================================================================
// --- Component for Asset Availability Table ---
// ====================================================================
const AssetAvailabilityTable = ({ workOrders, apiToken }) => {
    // ... (This component remains unchanged)
    const [combinedAssets, setCombinedAssets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filterDays, setFilterDays] = useState(null);
    const [customDays, setCustomDays] = useState('');
 
    useEffect(() => {
        const fetchAndCombineData = async () => {
            const cached = cache.get(CACHE_KEYS.ASSETS);
            if (cached && workOrders) {
                console.log('Using cached assets data for availability table');
                const workOrderInfoMap = new Map();
                if (Array.isArray(workOrders)) {
                    workOrders.forEach(wo => {
                        if (wo.asset_id != null) {
                            workOrderInfoMap.set(Number(wo.asset_id), { wo_start: wo.wo_start, wo_end: wo.wo_end, wo_no: wo.wo_no });
                        }
                    });
                }
                
                const activeAssets = cached.filter(asset => asset.active === true);
                const combinedData = activeAssets.map(asset => {
                    const workOrderInfo = workOrderInfoMap.get(asset.id) || { wo_start: null, wo_end: null, wo_no: null };
                    return { ...asset, ...workOrderInfo };
                });
                setCombinedAssets(combinedData);
                return;
            }

            setLoading(true);
            setError(null);
           
            const workOrderInfoMap = new Map();
            if (workOrders && Array.isArray(workOrders)) {
                workOrders.forEach(wo => {
                    if (wo.asset_id != null) {
                        workOrderInfoMap.set(Number(wo.asset_id), { wo_start: wo.wo_start, wo_end: wo.wo_end, wo_no: wo.wo_no });
                    }
                });
            }
           
            const ASSET_API_URL = `${process.env.REACT_APP_ASSET_SERVICE_BASE_URL}/api/assets/`;
            const ASSET_HEADERS = { 'accept': 'application/json', 'Authorization': apiToken };
 
            try {
                const response = await fetch(ASSET_API_URL, { headers: ASSET_HEADERS });
                if (!response.ok) {
                    throw new Error(`Failed to fetch assets, status: ${response.status}`);
                }
                const allAssets = await response.json();
                
                cache.set(CACHE_KEYS.ASSETS, allAssets);
                
                const activeAssets = allAssets.filter(asset => asset.active === true);

                const combinedData = activeAssets.map(asset => {
                    const workOrderInfo = workOrderInfoMap.get(asset.id) || { wo_start: null, wo_end: null, wo_no: null };
                    return { ...asset, ...workOrderInfo };
                });
               
                setCombinedAssets(combinedData);
            } catch (error) {
                console.error("Failed to fetch asset data:", error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };
 
        fetchAndCombineData();
    }, [workOrders, apiToken]);
 
    const filteredAssets = useMemo(() => {
        if (filterDays === null) return combinedAssets;
        const targetDate = new Date();
        targetDate.setDate(targetDate.getDate() + filterDays);
        targetDate.setHours(23, 59, 59, 999);
        return combinedAssets.filter(asset => {
            if (!asset.wo_end) return true;
            const woEndDate = new Date(asset.wo_end);
            return isNaN(woEndDate.getTime()) || woEndDate <= targetDate;
        });
    }, [combinedAssets, filterDays]);
 
    const formatData = (value) => {
        if (value === null || typeof value === 'undefined') return 'N/A';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'string' && value.includes('T')) {
            const date = new Date(value);
            if (!isNaN(date)) return date.toLocaleDateString('en-GB');
        }
        return value;
    };
 
    const handleCustomFilter = () => {
        const days = parseInt(customDays, 10);
        if (days > 0) setFilterDays(days);
    };
 
    const clearFilter = () => {
        setFilterDays(null);
        setCustomDays('');
    };
 
    return (
        <div className="filtered-table-container">
            <div className="table-header with-controls">
                <h3>Asset Availability</h3>
                <div className="availability-filter-controls">
                    <span>Available In:</span>
                    {[3, 7, 15, 30].map(days => (
                        <button key={days} onClick={() => setFilterDays(days)} className={filterDays === days ? 'active' : ''}>{days} Days</button>
                    ))}
                    <div className="custom-filter">
                        <input type="number" placeholder="Custom" value={customDays} onChange={(e) => setCustomDays(e.target.value)} min="1"/>
                        <button onClick={handleCustomFilter}>Go</button>
                    </div>
                    <button onClick={clearFilter}>Clear Filter</button>
                </div>
            </div>
            <div className="table-content-wrapper">
                {loading && <p className="loading-message">Loading Assets...</p>}
                {error && <p className="error-message">Error: {error}</p>}
                {!loading && !error && (
                    <>
                        {filteredAssets.length > 0 ? (
                            <div className="table-scroll-container">
                                <table className="data-table wide-table">
                                    <thead>
                                        <tr><th>ID</th><th>Asset Name</th><th>Asset Type</th><th>Capacity (MT)</th><th>Year of Purchase</th><th>Year of Manufacture</th><th>WO No</th><th>WO Start</th><th>WO End</th></tr>
                                    </thead>
                                    <tbody>
                                        {filteredAssets.map(asset => {
                                            const combinedAssetName = [asset.name, asset.serial_number, asset.asset_code, asset.registration_number].filter(Boolean).join(' | ');
                                            return (
                                                <tr key={asset.id}>
                                                    <td>{formatData(asset.id)}</td>
                                                    <td>{combinedAssetName || 'N/A'}</td>
                                                    <td>{formatData(asset.asset_type)}</td>
                                                    <td>{formatData(asset.capacity_mt)}</td>
                                                    <td>{formatData(asset.year_of_purchase)}</td>
                                                    <td>{formatData(asset.year_of_manufacture)}</td>
                                                    <td>{formatData(asset.wo_no)}</td>
                                                    <td>{formatData(asset.wo_start)}</td>
                                                    <td>{formatData(asset.wo_end)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (<p className="no-data-message">{filterDays !== null ? "No assets match the current availability filter." : "No active assets found."}</p>)}
                    </>
                )}
            </div>
        </div>
    );
};
 
// ====================================================================
// --- Component for the Work Orders Tab Content ---
// ====================================================================
const WorkOrderDashboardContent = ({
    loading, error, statusPieChartData, sectorBarChartData,
    topCustomersData, filteredWorkOrders, filters, onChartClick, onClearFilters, apiToken, onSetFullScreen
}) => {
    const statusChartRef = useRef();
    const sectorChartRef = useRef();
    const customerChartRef = useRef();

    const [statusChartType, setStatusChartType] = useState('pie');
    const [sectorChartType, setSectorChartType] = useState('bar');
    const [customerChartType, setCustomerChartType] = useState('bar');

    const handleChartClick = (filterType, chartData, event, chartRef) => {
        if (!chartRef.current) return;
        const elements = getElementAtEvent(chartRef.current, event);
        if (elements.length > 0) {
            const clickedLabel = chartData.labels[elements[0].index];
            onChartClick(filterType, clickedLabel);
        }
    };

    const handleExport = (chartRef, filename) => {
        if (!chartRef.current) return;
        const link = document.createElement('a');
        link.download = `${filename}.png`;
        link.href = chartRef.current.toBase64Image();
        link.click();
    };

    const renderChart = (type, data, chartRef, onClick, options) => {
        switch (type) {
            case 'bar':
                return <Bar ref={chartRef} data={data} options={options} onClick={onClick} />;
            case 'pie':
                return <Pie ref={chartRef} data={data} options={options} onClick={onClick} />;
            case 'doughnut':
                return <Doughnut ref={chartRef} data={data} options={options} onClick={onClick} />;
            default:
                return <p>Invalid chart type specified.</p>;
        }
    };
 
    const hasFilters = Object.keys(filters).length > 0;
   
    if (loading) return <div className="loading-error-container"><h2>Loading Dashboard...</h2></div>;
    if (error) return <div className="loading-error-container"><h2>Dashboard Error</h2><p>{error}</p></div>;
 
    const statusChartOptions = { responsive: true, plugins: { legend: { position: 'top' }, datalabels: { color: '#343a40', font: { weight: 'bold', size: 16 }, formatter: (value) => (value > 0 ? value : null) } } };
    const barChartOptions = { responsive: true, scales: { y: { beginAtZero: true } } };

    return (
        <main className="main-content">
             <div className="dashboard-title-bar">
                <h1 className="dashboard-header">Work Orders Dashboard</h1>
                {hasFilters && (<button onClick={onClearFilters} className="clear-filters-btn">Clear Filters</button>)}
            </div>
            <div className="dashboard-grid">
                <ChartWithOptions
                    title="Work Orders by Status"
                    chartType={statusChartType}
                    onChartTypeChange={setStatusChartType}
                    onExport={() => handleExport(statusChartRef, 'work-orders-by-status')}
                    onFullScreen={() => onSetFullScreen(statusPieChartData, statusChartOptions, statusChartType, 'Work Orders by Status')}
                >
                    {renderChart(
                        statusChartType,
                        statusPieChartData,
                        statusChartRef,
                        (event) => handleChartClick('status', statusPieChartData, event, statusChartRef),
                        statusChartOptions
                    )}
                </ChartWithOptions>
                
                <ChartWithOptions
                    title="Work Orders by Sector"
                    chartType={sectorChartType}
                    onChartTypeChange={setSectorChartType}
                    onExport={() => handleExport(sectorChartRef, 'work-orders-by-sector')}
                    onFullScreen={() => onSetFullScreen(sectorBarChartData, barChartOptions, sectorChartType, 'Work Orders by Sector')}
                >
                    {renderChart(
                        sectorChartType,
                        sectorBarChartData,
                        sectorChartRef,
                        (event) => handleChartClick('sector', sectorBarChartData, event, sectorChartRef),
                        barChartOptions
                    )}
                </ChartWithOptions>

                <ChartWithOptions
                    title="Top Customers by Order Value"
                    chartType={customerChartType}
                    onChartTypeChange={setCustomerChartType}
                    onExport={() => handleExport(customerChartRef, 'top-customers')}
                    onFullScreen={() => onSetFullScreen(topCustomersData, barChartOptions, customerChartType, 'Top Customers by Order Value')}
                >
                    {renderChart(
                        customerChartType,
                        topCustomersData,
                        customerChartRef,
                        (event) => handleChartClick('customer', topCustomersData, event, customerChartRef),
                        barChartOptions
                    )}
                </ChartWithOptions>

                <div className="chart-card table-card">
                    <FilteredWorkOrderTable workOrders={filteredWorkOrders} apiToken={apiToken} />
                </div>
                <div className="chart-card table-card">
                     <AssetAvailabilityTable workOrders={filteredWorkOrders} apiToken={apiToken} />
                </div>
            </div>
        </main>
    );
};

// ====================================================================
// --- Component for Invoice Data Table ---
// ====================================================================
const InvoiceDataTable = ({ invoices, workOrderMap, customerMap, userMap }) => {
    // ... (This component remains unchanged)
    const columns = useMemo(() => [
        { header: 'ID', key: 'id' }, { header: 'Doc No', key: 'doc_no' },
        { header: 'Customer Name', key: 'customer_name' }, { header: 'Customer ID', key: 'cust_id' },
        { header: 'Work Order ID', key: 'wo_id' }, { header: 'Work Order No', key: 'wo_no' },
        { header: 'Work Order Date', key: 'wo_date' }, { header: 'Project ID', key: 'project_id' },
        { header: 'Project Name', key: 'project_name' }, { header: 'Invoice Date', key: 'inv_date' },
        { header: 'Due Date', key: 'due_date' }, { header: 'Start Date', key: 'start_date' },
        { header: 'End Date', key: 'end_date' }, { header: 'Credit Period', key: 'cr_period' },
        { header: 'Taxable Amount', key: 'taxable_amt' },
        { header: 'CGST', key: 'tot_cgst' }, { header: 'SGST', key: 'tot_sgst' },
        { header: 'IGST', key: 'tot_igst' }, { header: 'Total Amount', key: 'amount' },
        { header: 'Approval Status', key: 'inv_approval_status' }, { header: 'Current Status', key: 'current_status' },
        { header: 'Created By', key: 'added_by' },
        { header: 'Created Date', key: 'add_date' }, { header: 'Updated By', key: 'updated_by' },
        { header: 'Updated Date', key: 'update_date' },
    ], []);

    const formatData = (value, key) => {
        const currencyKeys = ['taxable_amt', 'amount', 'tot_cgst', 'tot_sgst', 'tot_igst'];

        if (currencyKeys.includes(key)) {
            const numberValue = parseFloat(value ?? 0);
            return numberValue.toLocaleString('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        }

        if (key === 'cr_period') {
            return value ?? 0;
        }

        if (value === null || typeof value === 'undefined' || value === '') {
            return '-';
        }

        if (key === 'add_date' || key === 'update_date') {
            const date = new Date(value);
            return !isNaN(date) ? date.toLocaleString('en-GB') : '-';
        }

        if (typeof value === 'string' && (key.includes('date') || key.includes('Date'))) {
            const date = new Date(value);
            return !isNaN(date) ? date.toLocaleDateString('en-GB') : '-';
        }

        return value;
    };
    
    const sortedInvoices = useMemo(() => {
        if (!Array.isArray(invoices)) return [];
        return [...invoices].sort((a, b) => new Date(b.inv_date || b.add_date) - new Date(a.inv_date || a.add_date));
    }, [invoices]);

    if (sortedInvoices.length === 0) {
        return (
            <div className="filtered-table-container">
                <EmptyState
                    title="No invoices"
                    description="No invoices match the current date range, status, or search filter."
                />
            </div>
        );
    }

    return (
        <div className="filtered-table-container">
            <div className="table-content-wrapper">
                <div className="table-scroll-container">
                    <table className="data-table wide-table">
                        <thead>
                            <tr>{columns.map(col => <th key={col.key}>{col.header}</th>)}</tr>
                        </thead>
                        <tbody>
                            {sortedInvoices.map((invoice) => {
                                const relatedWorkOrder = workOrderMap.get(invoice.wo_id) || {};
                                return (
                                    <tr key={invoice.id}>
                                        {columns.map(col => {
                                            let rawValue;
                                            switch(col.key) {
                                                case 'customer_name':
                                                    rawValue = customerMap.get(invoice.cust_id);
                                                    break;
                                                case 'added_by':
                                                    rawValue = userMap.get(String(invoice.added_by)) ?? invoice.added_by;
                                                    break;
                                                case 'updated_by':
                                                    rawValue = userMap.get(String(invoice.updated_by)) ?? invoice.updated_by;
                                                    break;
                                                case 'project_id': case 'project_name': case 'wo_no': case 'wo_date':
                                                    rawValue = relatedWorkOrder[col.key];
                                                    break;
                                                default:
                                                    rawValue = invoice[col.key];
                                            }
                                            
                                            const cellValue = formatData(rawValue, col.key);
                                            const rightAlignKeys = ['taxable_amt', 'amount', 'tot_cgst', 'tot_sgst', 'tot_igst', 'cr_period'];
                                            const shouldRightAlign = rightAlignKeys.includes(col.key);
                                            
                                            return <td key={col.key} style={{ textAlign: shouldRightAlign ? 'right' : 'left' }}>{cellValue}</td>;
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
 
// ====================================================================
// --- Component for the Invoices Tab ---
// ====================================================================
const InvoiceDashboardContent = ({ invoices, workOrders, loading, error, customerMap, userMap, onSetFullScreen }) => {
    const today = new Date();

// Calculate financial year start date (April 1st)
const getFinancialYearStart = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-indexed (0 = January, 3 = April)
    
    let startYear;
    // If we're in January, February, or March, use April 1st of previous year
    if (currentMonth < 3) {
        startYear = currentYear - 1;
    } else {
        // If we're in April or later, use April 1st of current year
        startYear = currentYear;
    }
    
    // Create date string in YYYY-MM-DD format to avoid timezone issues
    const year = startYear;
    const month = '04'; // April
    const day = '01';
    
    return `${year}-${month}-${day}`;
};

const financialYearStart = getFinancialYearStart();
const formatDateForInput = (date) => date.toISOString().split('T')[0];

const [startDate, setStartDate] = useState(financialYearStart);
const [endDate, setEndDate] = useState(formatDateForInput(today));


    
    const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilterType, setDateFilterType] = useState('invoiceDate');
    const tableRef = useRef(null);

    // Chart-specific states and refs
    const statusPieChartRef = useRef(null);
    const customerBarChartRef = useRef(null);
    const [customerChartType, setCustomerChartType] = useState('bar');
    const [statusChartType, setStatusChartType] = useState('pie');

    const workOrderMap = useMemo(() => {
        const map = new Map();
        if (Array.isArray(workOrders)) {
            workOrders.forEach(wo => {
                if (wo.wo_id && !map.has(wo.wo_id)) {
                    map.set(wo.wo_id, wo);
                }
            });
        }
        return map;
    }, [workOrders]);
    
    const dateFilteredInvoices = useMemo(() => {
        if (!Array.isArray(invoices) || !startDate || !endDate) return [];
        
        const rangeStart = new Date(startDate);
        rangeStart.setHours(0, 0, 0, 0);
        const rangeEnd = new Date(endDate);
        rangeEnd.setHours(23, 59, 59, 999);

        return invoices.filter(inv => {
            if (dateFilterType === 'invoiceDate') {
                const invoiceDate = new Date(inv.inv_date || inv.add_date);
                return !isNaN(invoiceDate) && invoiceDate >= rangeStart && invoiceDate <= rangeEnd;
            } else {
                if (!inv.start_date || !inv.end_date) return false;
                
                const periodStart = new Date(inv.start_date);
                const periodEnd = new Date(inv.end_date);
                
                if (isNaN(periodStart.getTime()) || isNaN(periodEnd.getTime())) return false;
                
                return periodStart >= rangeStart && periodEnd <= rangeEnd;
            }
        });
    }, [invoices, startDate, endDate, dateFilterType]);
 
    const dashboardData = useMemo(() => {
        const EMPTY_CHART_DATA = { labels: [], datasets: [{ data: [] }] };
        const baseKpis = { totalInvoices: 0, totalTaxable: 0, totalGst: 0, avgCreditPeriod: 0 };

        if (dateFilteredInvoices.length === 0) {
            return { kpis: baseKpis, customerData: EMPTY_CHART_DATA, statusPieData: EMPTY_CHART_DATA };
        }
 
        const statusCountsForPie = dateFilteredInvoices.reduce((acc, inv) => {
            const status = inv.current_status || 'Unknown';
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});

        const sortedStatusLabels = Object.keys(statusCountsForPie).sort();
        
        const statusPieData = {
            labels: sortedStatusLabels,
            datasets: [{ 
                data: sortedStatusLabels.map(label => statusCountsForPie[label]),
                backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#F7464A'], 
                borderColor: '#fff', 
                borderWidth: 2 
            }]
        };
        
        const customerAggregates = dateFilteredInvoices.reduce((acc, inv) => {
            const customerName = customerMap.get(inv.cust_id) || `Customer ID: ${inv.cust_id}`;
            acc[customerName] = (acc[customerName] || 0) + (Number(inv.amount) || 0);
            return acc;
        }, {});
        
        const sortedCustomers = Object.entries(customerAggregates)
            .filter(([, amount]) => amount > 0)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 10);
        const customerData = { 
            labels: sortedCustomers.map(([name]) => name), 
            datasets: [{ label: 'Total Invoice Amount', data: sortedCustomers.map(([, amount]) => amount), backgroundColor: 'rgba(153, 102, 255, 0.6)' }] 
        };
       
        return {
            kpis: {
                totalInvoices: dateFilteredInvoices.length,
                totalTaxable: dateFilteredInvoices.reduce((sum, inv) => sum + (Number(inv.taxable_amt) || 0), 0),
                totalGst: dateFilteredInvoices.reduce((sum, inv) => sum + (Number(inv.tot_cgst) || 0) + (Number(inv.tot_sgst) || 0) + (Number(inv.tot_igst) || 0), 0),
                avgCreditPeriod: (dateFilteredInvoices.map(inv => Number(inv.cr_period)).filter(p => p > 0).reduce((a, b, _, arr) => a + b / arr.length, 0)).toFixed(2) || 0
            },
            customerData, statusPieData
        };
    }, [dateFilteredInvoices, customerMap]);
    
    const statusFilteredInvoices = useMemo(() => {
        if (!selectedStatusFilter) return dateFilteredInvoices;
        return dateFilteredInvoices.filter(invoice => invoice.current_status === selectedStatusFilter);
    }, [dateFilteredInvoices, selectedStatusFilter]);

    const finalFilteredInvoices = useMemo(() => {
        if (!searchTerm.trim()) return statusFilteredInvoices;
        const lowercasedFilter = searchTerm.toLowerCase();
        return statusFilteredInvoices.filter(invoice => {
            const workOrder = workOrderMap.get(invoice.wo_id) || {};
            const searchableFields = [
                invoice.id, invoice.doc_no, invoice.cust_id, customerMap.get(invoice.cust_id),
                invoice.wo_id, workOrder.wo_no, workOrder.project_id, workOrder.project_name,
                invoice.inv_approval_status, invoice.current_status,
                userMap.get(String(invoice.added_by)), userMap.get(String(invoice.updated_by))
            ];
            return searchableFields.some(field => field && String(field).toLowerCase().includes(lowercasedFilter));
        });
    }, [statusFilteredInvoices, searchTerm, workOrderMap, customerMap, userMap]);

    const handleStatusPieClick = (event) => {
        if (!statusPieChartRef.current) return;
        const elements = getElementAtEvent(statusPieChartRef.current, event);
        if (elements.length > 0) {
            const clickedStatus = dashboardData.statusPieData.labels[elements[0].index];
            setSelectedStatusFilter(prev => prev === clickedStatus ? null : clickedStatus);
            setTimeout(() => tableRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        } else {
            setSelectedStatusFilter(null);
        }
    };
    
    const convertToCSVAndDownload = (dataToExport, filename) => {
        if (dataToExport.length === 0) {
            alert("No data available to export.");
            return;
        }

        const columns = [
            { header: 'ID', key: 'id' }, { header: 'Doc No', key: 'doc_no' }, { header: 'Customer Name', key: 'customer_name' },
            { header: 'Customer ID', key: 'cust_id' }, { header: 'Work Order ID', key: 'wo_id' }, { header: 'Work Order No', key: 'wo_no' },
            { header: 'Work Order Date', key: 'wo_date' }, { header: 'Project Name', key: 'project_name' }, { header: 'Invoice Date', key: 'inv_date' },
            { header: 'Due Date', key: 'due_date' }, { header: 'Total Amount', key: 'amount' }, { header: 'Current Status', key: 'current_status' },
            { header: 'Created By', key: 'added_by' }, { header: 'Created Date', key: 'add_date' }, { header: 'Updated By', key: 'updated_by' },
            { header: 'Updated Date', key: 'update_date' },
        ];

        const replacer = (key, value) => value === null ? '' : value;
        const header = columns.map(c => c.header).join(',');
        const rows = dataToExport.map(invoice => {
            const relatedWorkOrder = workOrderMap.get(invoice.wo_id) || {};
            return columns.map(col => {
                let cellData;
                switch (col.key) {
                    case 'customer_name': cellData = customerMap.get(invoice.cust_id) || 'N/A'; break;
                    case 'added_by': cellData = userMap.get(String(invoice.added_by)) || invoice.added_by || 'N/A'; break;
                    case 'updated_by': cellData = userMap.get(String(invoice.updated_by)) || invoice.updated_by || 'N/A'; break;
                    case 'wo_no': case 'wo_date': case 'project_name': cellData = relatedWorkOrder[col.key]; break;
                    default: cellData = invoice[col.key];
                }
                const stringValue = JSON.stringify(replacer(col.key, cellData), replacer);
                return stringValue.replace(/"/g, '""').replace(/^""|""$/g, '"');
            }).join(',');
        });

        const csv = [header, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `${filename}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportAll = () => convertToCSVAndDownload(statusFilteredInvoices, 'all_invoices_export');
    const handleExportByFilter = () => convertToCSVAndDownload(finalFilteredInvoices, 'filtered_invoices_export');

    const handleExportChart = (chartRef, filename) => {
        if (!chartRef.current) return;
        const link = document.createElement('a');
        link.download = `${filename}.png`;
        link.href = chartRef.current.toBase64Image();
        link.click();
    };

    const renderChart = (type, data, chartRef, onClick, options) => {
        switch (type) {
            case 'bar':
                return <Bar ref={chartRef} data={data} options={options} onClick={onClick} />;
            case 'pie':
                return <Pie ref={chartRef} data={data} options={options} onClick={onClick} />;
            case 'doughnut':
                return <Doughnut ref={chartRef} data={data} options={options} onClick={onClick} />;
            default:
                return <p>Invalid chart type specified.</p>;
        }
    };

    if (loading) return <div className="loading-error-container"><h2>Loading Invoice Dashboard...</h2></div>;
    if (error) return <div className="loading-error-container"><h2>Dashboard Error</h2><p>{error}</p></div>;
 
    const formatCurrency = (value) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);
    const pieOptions = { responsive: true, plugins: { legend: { position: 'bottom' }, datalabels: { color: '#fff', font: { weight: 'bold' }, formatter: (value) => (value > 0 ? value : '') } } };
    const barOptions = { responsive: true, scales: { y: { beginAtZero: true } } };
 
    return (
        <main className="main-content">
            <div className="filter-bar">
                <div className="filter-group">
                    <div className="filter-item">
                        <input type="radio" id="invoiceDate" name="dateFilterType" value="invoiceDate" checked={dateFilterType === 'invoiceDate'} onChange={(e) => setDateFilterType(e.target.value)} />
                        <label htmlFor="invoiceDate">Invoice Date</label>
                    </div>
                    <div className="filter-item">
                        <input type="radio" id="invoicePeriod" name="dateFilterType" value="invoicePeriod" checked={dateFilterType === 'invoicePeriod'} onChange={(e) => setDateFilterType(e.target.value)} />
                        <label htmlFor="invoicePeriod">Invoice Period</label>
                    </div>
                </div>
                <div className="filter-item"><label htmlFor="start-date">Start Date:</label><input type="date" id="start-date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
                <div className="filter-item"><label htmlFor="end-date">End Date:</label><input type="date" id="end-date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div>
            </div>
           
            <div className="kpi-grid">
                <div className="kpi-card"><h4>Total Invoices</h4><p>{dashboardData.kpis.totalInvoices}</p></div>
                <div className="kpi-card"><h4>Total Taxable Amount</h4><p>{formatCurrency(dashboardData.kpis.totalTaxable)}</p></div>
                <div className="kpi-card"><h4>Total GST Collected</h4><p>{formatCurrency(dashboardData.kpis.totalGst)}</p></div>
                <div className="kpi-card"><h4>Avg. Credit Period</h4><p>{dashboardData.kpis.avgCreditPeriod} Days</p></div>
            </div>
           
            <div className="dashboard-grid">
                <ChartWithOptions
                    title="Customer Wise Invoices (Top 10 by Amount)"
                    cardClassName="large-card"
                    chartType={customerChartType}
                    onChartTypeChange={setCustomerChartType}
                    onExport={() => handleExportChart(customerBarChartRef, 'customer-invoices')}
                    onFullScreen={() => onSetFullScreen(dashboardData.customerData, barOptions, customerChartType, 'Customer Wise Invoices')}
                >
                    {dateFilteredInvoices.length > 0 && dashboardData.customerData.labels.length > 0 ? (
                        renderChart(customerChartType, dashboardData.customerData, customerBarChartRef, null, barOptions)
                    ) : (
                        <p className="no-data-message">No customer invoice data with positive amounts for selected date range.</p>
                    )}
                </ChartWithOptions>
                
                <ChartWithOptions
                    title="Invoices by Status"
                    chartType={statusChartType}
                    onChartTypeChange={setStatusChartType}
                    onExport={() => handleExportChart(statusPieChartRef, 'invoices-by-status')}
                    onFullScreen={() => onSetFullScreen(dashboardData.statusPieData, pieOptions, statusChartType, 'Invoices by Status')}
                >
                   {dateFilteredInvoices.length > 0 ? (
                        renderChart(statusChartType, dashboardData.statusPieData, statusPieChartRef, handleStatusPieClick, pieOptions)
                    ) : (
                        <p className="no-data-message">No data available.</p>
                    )}
                </ChartWithOptions>
            </div>

            <div ref={tableRef} className="invoice-table-section" style={{ marginTop: '2rem' }}>
                <div className="table-header with-controls">
                    <h3>All Invoices {selectedStatusFilter && `(Status: ${selectedStatusFilter})`} {searchTerm && `(Filtered)`}</h3>
                    <div className="table-actions">
                        {selectedStatusFilter && (<button onClick={() => setSelectedStatusFilter(null)} className="clear-filters-btn small">Clear Status Filter</button>)}
                        <input type="text" placeholder="Search invoices..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-bar" />
                        <button onClick={handleExportByFilter} className="export-btn">Export by Filter</button>
                        <button onClick={handleExportAll} className="export-btn">Export All</button>
                    </div>
                </div>
                <InvoiceDataTable invoices={finalFilteredInvoices} workOrderMap={workOrderMap} customerMap={customerMap} userMap={userMap} />
            </div>
        </main>
    );
};
 
// ====================================================================
// --- Main Dashboard Component ---
// ====================================================================
const Dashboard = () => {
    const [activeTab, setActiveTab] = useState('workOrder');
    const [workOrderTableReport, setWorkOrderTableReport] = useState({ columns: [], data: [] });
    // START: Added state for the new data source
    const [allWorkOrdersForStatusChart, setAllWorkOrdersForStatusChart] = useState([]);
    // END: Added state for the new data source
    const [invoices, setInvoices] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [users, setUsers] = useState([]); 
    const [filters, setFilters] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fullScreenChart, setFullScreenChart] = useState(null);
 
    const API_TOKEN = 'Bearer ' + localStorage.getItem('zTaskAuthToken');
 
    const handleApiResponse = async (response) => {
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API call to ${response.url} failed with status ${response.status}: ${response.statusText}. Response: ${errorText.substring(0, 200)}`);
        }
        return response.json();
    };
 
    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            setError(null);
 
            const REPORT_API_URL = `${getReportsApiBaseUrl()}/api/reports/run`;
            // START: Added URL for the new API
            const ALL_WORK_ORDERS_API_URL = `${getNodeServiceBaseUrl()}/work-order/api/work-orders/`;
            const INVOICE_API_URL = `${getNodeServiceBaseUrl()}/invoice/api/invoices/`;
            const CUSTOMER_API_URL = `${getNodeServiceBaseUrl()}/api/get-column/customers/name`;
            const USER_API_URL = `${getNodeServiceBaseUrl()}/api/get-column/users/name`;
 
            const HEADERS = { 'accept': 'application/json', 'Authorization': API_TOKEN };
            const TABLE_REPORT_BODY = { "report_name": "work_order_table", "module_name": "Workorder" };
           
            try {
                const cachedWorkOrders = cache.get(CACHE_KEYS.WORK_ORDERS);
                const cachedAllWorkOrdersStatus = cache.get(CACHE_KEYS.ALL_WORK_ORDERS_STATUS);
                const cachedInvoices = cache.get(CACHE_KEYS.INVOICES);
                const cachedCustomers = cache.get(CACHE_KEYS.CUSTOMERS);
                const cachedUsers = cache.get(CACHE_KEYS.USERS);

                const promises = [];
                const promiseKeys = [];

                if (cachedWorkOrders) {
                    setWorkOrderTableReport(cachedWorkOrders);
                } else {
                    promises.push(
                      fetch(REPORT_API_URL, {
                        method: 'POST',
                        headers: { ...HEADERS, 'Content-Type': 'application/json' },
                        body: JSON.stringify(TABLE_REPORT_BODY),
                      })
                        .then(async (response) => {
                          if (!response.ok) {
                            console.warn('work_order_table report unavailable', response.status);
                            return { columns: [], data: [] };
                          }
                          return response.json();
                        })
                        .catch((err) => {
                          console.warn('work_order_table report failed', err);
                          return { columns: [], data: [] };
                        })
                    );
                    promiseKeys.push('workOrders');
                }
                
                // START: Added fetch logic for the new API
                if (cachedAllWorkOrdersStatus) {
                    setAllWorkOrdersForStatusChart(cachedAllWorkOrdersStatus);
                } else {
                    promises.push(fetch(ALL_WORK_ORDERS_API_URL, { headers: HEADERS }).then(handleApiResponse));
                    promiseKeys.push('allWorkOrdersStatus');
                }
                // END: Added fetch logic for the new API

                if (cachedInvoices) {
                    setInvoices(cachedInvoices);
                } else {
                    promises.push(fetch(INVOICE_API_URL, { headers: HEADERS }).then(handleApiResponse));
                    promiseKeys.push('invoices');
                }

                if (cachedCustomers) {
                    setCustomers(cachedCustomers);
                } else {
                    promises.push(fetch(CUSTOMER_API_URL, { headers: HEADERS }).then(handleApiResponse));
                    promiseKeys.push('customers');
                }

                if (cachedUsers) {
                    setUsers(cachedUsers);
                } else {
                    promises.push(fetch(USER_API_URL, { headers: HEADERS }).then(handleApiResponse));
                    promiseKeys.push('users');
                }

                if (promises.length > 0) {
                    const results = await Promise.all(promises);
                    
                    results.forEach((data, index) => {
                        const key = promiseKeys[index];
                        switch(key) {
                            case 'workOrders':
                                const workOrderData = data && data.columns && data.data ? data : { columns: [], data: [] };
                                cache.set(CACHE_KEYS.WORK_ORDERS, workOrderData);
                                setWorkOrderTableReport(workOrderData);
                                break;
                            // START: Added response handler for the new API
                            case 'allWorkOrdersStatus':
                                const allWoData = Array.isArray(data) ? data : [];
                                cache.set(CACHE_KEYS.ALL_WORK_ORDERS_STATUS, allWoData);
                                setAllWorkOrdersForStatusChart(allWoData);
                                break;
                            // END: Added response handler for the new API
                            case 'invoices':
                                const invoiceData = Array.isArray(data) ? data : [];
                                cache.set(CACHE_KEYS.INVOICES, invoiceData);
                                setInvoices(invoiceData);
                                break;
                            case 'customers':
                                const customerData = Array.isArray(data) ? data : [];
                                cache.set(CACHE_KEYS.CUSTOMERS, customerData);
                                setCustomers(customerData);
                                break;
                            case 'users':
                                const userData = Array.isArray(data) ? data : [];
                                cache.set(CACHE_KEYS.USERS, userData);
                                setUsers(userData);
                                break;
                            default:
                                break;
                        }
                    });
                }

            } catch (e) {
                console.error("Failed to fetch dashboard data:", e);
                setError(e.message);
            } finally {
                setLoading(false);
            }
        };
        fetchAllData();
    }, [API_TOKEN]); // Removed API_TOKEN from dependency array to avoid re-fetch on token change during session
 
    const handleChartClick = (filterType, clickedLabel) => setFilters(prev => ({ ...prev, [filterType]: clickedLabel }));
    const clearFilters = () => setFilters({});
    
    const handleSetFullScreen = (data, options, type, title) => {
        setFullScreenChart({ data, options, type, title });
    };
    const handleCloseFullScreen = () => setFullScreenChart(null);
   
    const customerMap = useMemo(() => {
        const map = new Map();
        customers.forEach(customer => map.set(customer.id, customer.value));
        return map;
    }, [customers]);

    const userMap = useMemo(() => {
        const map = new Map();
        users.forEach(user => map.set(user.id, user.value));
        return map;
    }, [users]);

    const uniqueWorkOrders = useMemo(() => {
        if (!Array.isArray(workOrderTableReport.data)) return [];
        const uniqueWoMap = new Map();
        workOrderTableReport.data.forEach(order => {
            if (order.wo_id && !uniqueWoMap.has(order.wo_id)) {
                uniqueWoMap.set(order.wo_id, { ...order, tot_value: parseFloat(order.tot_value) || 0 });
            }
        });
        return Array.from(uniqueWoMap.values());
    }, [workOrderTableReport.data]);
 
    // START: Updated logic for the status pie chart
    const statusPieChartData = useMemo(() => {
        const labels = ['Open', 'Closed'];
        const statusCounts = allWorkOrdersForStatusChart.reduce((acc, order) => {
            const status = order.wo_open_close;
            if (status === 'Open') {
                acc.Open++;
            } else if (status === 'Closed') {
                acc.Closed++;
            }
            return acc;
        }, { 'Open': 0, 'Closed': 0 });
        
        return {
            labels: labels,
            datasets: [{
                data: labels.map(label => statusCounts[label]),
                backgroundColor: ['#80d8ff', '#ff8a80'], // Open = blue, Closed = red
                borderColor: '#ffffff',
                borderWidth: 3
            }]
        };
    }, [allWorkOrdersForStatusChart]);
    // END: Updated logic for the status pie chart
 
    const sectorBarChartData = useMemo(() => {
        const sectorData = uniqueWorkOrders.reduce((acc, wo) => {
            const sectorName = wo.sector_name || 'Unknown Sector';
            acc[sectorName] = acc[sectorName] || { count: 0, totalValue: 0 };
            acc[sectorName].count++;
            acc[sectorName].totalValue += wo.tot_value;
            return acc;
        }, {});
        const labels = Object.keys(sectorData).sort();
        return { labels, datasets: [
            { label: 'Number of Orders', data: labels.map(l => sectorData[l].count), backgroundColor: 'rgba(255, 159, 64, 0.8)' },
            { label: 'Total Value', data: labels.map(l => sectorData[l].totalValue), backgroundColor: 'rgba(75, 192, 192, 0.8)' }
        ] };
    }, [uniqueWorkOrders]);
   
    const topCustomersData = useMemo(() => {
        const customerAggregates = uniqueWorkOrders.reduce((acc, wo) => {
            acc[wo.customer_name || 'Unknown Customer'] = (acc[wo.customer_name || 'Unknown Customer'] || 0) + wo.tot_value;
            return acc;
        }, {});
        const sortedCustomers = Object.entries(customerAggregates).sort(([, a], [, b]) => b - a).slice(0, 5);
        return { labels: sortedCustomers.map(([label]) => label), datasets: [{ label: 'Total Order Value', data: sortedCustomers.map(([, value]) => value), backgroundColor: 'rgba(54, 162, 235, 0.8)' }] };
    }, [uniqueWorkOrders]);
 
    const filteredWorkOrders = useMemo(() => {
        if (Object.keys(filters).length === 0) return workOrderTableReport.data || [];
        const woIdsToKeep = new Set(uniqueWorkOrders
            .filter(wo => (!filters.status || wo.wo_open_close === filters.status) && (!filters.sector || wo.sector_name === filters.sector) && (!filters.customer || wo.customer_name === filters.customer))
            .map(wo => wo.wo_id)
        );
        return (workOrderTableReport.data || []).filter(order => woIdsToKeep.has(order.wo_id));
    }, [workOrderTableReport.data, uniqueWorkOrders, filters]);
 
    return (
        <div className="dashboard-container">
            <div className="tabs-container">
                <button className={`tab-button ${activeTab === 'workOrder' ? 'active' : ''}`} onClick={() => setActiveTab('workOrder')}>Work Orders</button>
                <button className={`tab-button ${activeTab === 'invoice' ? 'active' : ''}`} onClick={() => setActiveTab('invoice')}>Invoices</button>
            </div>
            <div className="tab-content">
                {activeTab === 'workOrder' && (
                    <WorkOrderDashboardContent
                        loading={loading} error={error} statusPieChartData={statusPieChartData}
                        sectorBarChartData={sectorBarChartData}
                        topCustomersData={topCustomersData} filteredWorkOrders={filteredWorkOrders}
                        filters={filters} onChartClick={handleChartClick} onClearFilters={clearFilters}
                        apiToken={API_TOKEN}
                        onSetFullScreen={handleSetFullScreen}
                    />
                )}
                {activeTab === 'invoice' && (
                    <InvoiceDashboardContent 
                        invoices={invoices} 
                        workOrders={workOrderTableReport.data}
                        loading={loading} 
                        error={error}
                        customerMap={customerMap}
                        userMap={userMap}
                        onSetFullScreen={handleSetFullScreen}
                    />
                )}
            </div>

            {fullScreenChart && (
                <div className="fullscreen-modal-overlay" onClick={handleCloseFullScreen}>
                  <div className="fullscreen-modal-content" onClick={(e) => e.stopPropagation()}>
                    <button className="fullscreen-modal-close" onClick={handleCloseFullScreen}>&times;</button>
                    <h3 className="fullscreen-modal-title">{fullScreenChart.title}</h3>
                    <div className="fullscreen-chart-container">
                      {fullScreenChart.type === 'bar' && <Bar data={fullScreenChart.data} options={{...fullScreenChart.options, maintainAspectRatio: false}} />}
                      {fullScreenChart.type === 'pie' && <Pie data={fullScreenChart.data} options={{...fullScreenChart.options, maintainAspectRatio: false}} />}
                      {fullScreenChart.type === 'doughnut' && <Doughnut data={fullScreenChart.data} options={{...fullScreenChart.options, maintainAspectRatio: false}} />}
                    </div>
                  </div>
                </div>
              )}
        </div>
    );
};
 
export default Dashboard;
